import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
} from "@mui/material"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import PhoneIcon from "@mui/icons-material/Phone"
import type { Voucher } from "../types/voucher"
import { useState, useMemo, useEffect } from "react"
import useOdooApi from "../hooks/useOdooApi"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import AutorenewIcon from "@mui/icons-material/Autorenew"

interface Props {
  open: boolean
  voucherData: Voucher | null
  onClose: () => void
  onConfirm: () => void
}

export default function SendVoucherPopup({
  open,
  onClose,
  voucherData,
}: Props) {
  const [emailSend, setEmailSend] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle")
  const [countdown, setCountdown] = useState(2)
  const [validEmail, setValitEmail] = useState('')

  const { transferVoucher } = useOdooApi()

  function readJsonFromStorage<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  }

  const userData = useMemo(
    () =>
      readJsonFromStorage<{ patient_id?: number; email?: string }>("user") ??
      {},
    []
  )

  useEffect(() => {
    if (status !== "success") return

    setCountdown(2)

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          window.location.reload()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [status])

  const formatDate = (value: string): string => {
    if (!value || value === "-") return "-"
    const date = new Date(value)
    if (isNaN(date.getTime())) return value
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const isValidEmail = (email: string): boolean => {
    const trimmed = email.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(trimmed)
  }

  const handleTransfer = async () => {
    if (!voucherData?.code) return
    if (!userData?.patient_id) return
    if (!emailSend) {
      setValitEmail('กรุณากรอก Email ผู้รับ')
      return
    }

    const email = emailSend.trim()
    if (!isValidEmail(email)) {
      setValitEmail('รูปแบบ Email ไม่ถูกต้อง')
      return
    }

    setStatus("loading")

    try {
      const response = await transferVoucher({
        patient_id: userData.patient_id,
        code: voucherData.code,
        email,
      })

      if (Number(response.code) >= 300) {
        setStatus("idle")
        return
      }

      setStatus("success")

      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (error) {
      console.error(error)
      setStatus("idle")
    }
  }

  return (
    <Dialog
      open={open}
      onClose={status === "loading" ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          height: "100%",
          background:
            "linear-gradient(180deg, #F1E6DB 0%, #E8B89F 150%)",
        },
      }}
    >
      {status === "idle" && (
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="center" mb={2}>
            <Typography fontWeight={600} fontSize={24} color="#717AAE">
              ส่งต่อ VOUCHER
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              borderRadius: 4,
              backgroundColor: "#E5E5E5",
              aspectRatio: "16/5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              overflow: "hidden",
            }}
          >
            {voucherData?.details?.banner ? (
              <Box
                component="img"
                src={voucherData.details.banner}
                alt="voucher-banner"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography color="text.secondary">
                Voucher Image
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              background: "#F3F4F6",
              borderRadius: 3,
              p: 2,
              mb: 3,
            }}
          >
            <Typography fontSize={14} mb={1}>
              Voucher ที่ต้องการส่งต่อ
            </Typography>
            <Typography fontWeight={600} fontSize={18} mb={1}>
              {voucherData?.name || ""}
            </Typography>
            <Typography fontSize={14} color="text.secondary">
              วันที่ซื้อ :{" "}
              {formatDate(voucherData?.details?.issue_date || "-")}
              &nbsp;&nbsp;
            </Typography>
            <Typography fontSize={14} color="text.secondary">
              สามารถแลกรับสิทธิ์และเข้าใช้บริการได้ภายในวันที่  :{" "}
              {formatDate(
                voucherData?.details?.expiration_date || "-"
              )}
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography fontWeight={500} mb={1}>
              อีเมลผู้รับ
            </Typography>
            <TextField
              fullWidth
              value={emailSend}
              onChange={(e) => setEmailSend(e.target.value)}
              placeholder="example@gmail.com"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F4F4F5",
                  "& fieldset": {
                    borderColor: "#E7E7E7",
                  },
                  "&:hover fieldset": {
                    borderColor: "#E7E7E7",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#E7E7E7",
                  },
                },
              }}
            />
            {validEmail && (
              <Typography
                sx={{
                  mt: 1,
                  px: 1.5,
                  width: '100%',
                  py: 0.5,
                  borderRadius: 1.5,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#B91C1C",
                  backgroundColor: "#FEE2E2",
                  border: "1px solid #FCA5A5",
                  display: "inline-block",
                }}
              >
                {validEmail}
              </Typography>
            )}

          </Box>



          <Box
            sx={{
              borderRadius: 3,
              p: 2,
              mb: 4,
              background:
                "linear-gradient(135deg, rgb(191,162,202), rgb(245,163,156), rgb(239,182,153))",
              color: "#fff",
            }}
          >
            <Typography fontWeight={600} mb={1} textAlign="center">
              ผู้สั่งซื้อ
            </Typography>
            <Typography fontWeight={500} textAlign="center">
              {voucherData?.details?.full_name || ""}
            </Typography>

            <Box display="flex" justifyContent="center" gap={1} mt={1}>
              <MailOutlineIcon fontSize="small" />
              <Typography fontSize={14}>
                {voucherData?.details?.email || ""}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="center" gap={1} mt={1}>
              <PhoneIcon fontSize="small" />
              <Typography fontSize={14}>
                {voucherData?.details?.phone || ""}
              </Typography>
            </Box>
          </Box>

          <button
            className="w-full py-3 rounded-xl mb-3 bg-[#F19A9B] text-white font-semibold cursor-pointer"
            onClick={handleTransfer}
          >
            ยืนยันการส่ง
          </button>

          <button
            className="w-full py-3 rounded-xl bg-zinc-200 text-zinc-700 font-semibold hover:bg-zinc-300 transition cursor-pointer"
            onClick={onClose}
          >
            ยกเลิก
          </button>
        </DialogContent>
      )}

      {status === "loading" && (
        <DialogContent
          sx={{
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            textAlign: "center",
          }}
        >
          <AutorenewIcon
            sx={{
              fontSize: 48,
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography fontSize={20} fontWeight={600}>
            กำลังส่ง Voucher
          </Typography>
          <Typography color="text.secondary" fontSize={14}>
            กรุณารอสักครู่ ระบบกำลังดำเนินการ...
          </Typography>
        </DialogContent>
      )}
      {status === "success" && (
        <DialogContent
          sx={{
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            textAlign: "center",
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 64,
              color: "#16A34A",
            }}
          />

          <Typography
            fontSize={22}
            fontWeight={600}
            color="#16A34A"
          >
            ส่งสำเร็จ
          </Typography>

          <Typography color="text.secondary" fontSize={15}>
            ระบบกำลังอัพเดทข้อมูล{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {countdown}
            </Box>{" "}
            วินาที
          </Typography>
        </DialogContent>
      )}
    </Dialog>
  )
}